import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  AlertTriangle,
  ArchiveX,
  Ban,
  Banknote,
  BarChart3,
  BookOpen,
  Boxes,
  Building2,
  Calendar,
  CalendarCheck,
  CalendarDays,
  CheckCircle,
  Edit2,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CreditCard,
  Eye,
  FileText,
  FlaskConical,
  FolderOpen,
  HeartPulse,
  Image as ImageIcon,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  Link2,
  ListChecks,
  ListOrdered,
  MapPin,
  Monitor,
  NotebookPen,
  Package,
  PackageCheck,
  PackagePlus,
  PackageX,
  Palette,
  PauseCircle,
  Pencil,
  Percent,
  PhoneCall,
  Receipt,
  PhoneOutgoing,
  Pill,
  Pill as PillIcon,
  Plus,
  Printer,
  RotateCcw,
  RotateCw,
  Save,
  ScrollText,
  Settings,
  Share2,
  Shield,
  ShieldCheck,
  Stethoscope,
  StickyNote,
  Tag,
  Ticket,
  TicketPlus,
  ToggleLeft,
  Trash2,
  Unlink,
  Upload,
  UserCheck,
  UserCog,
  UserX,
  Users,
  Syringe,
  Wallet,
  Wrench,
  X,
  XCircle,
} from 'lucide-react';
import { rolePermissionAPI } from '../services/api';
import { useToast } from './UIProvider';
import Select from './Select';
import { HIDDEN_PERMISSION_MODULES, HIDDEN_PERMISSION_ACTIONS } from '../config/features';
import { scrollContentToTop } from '../utils/scroll';
import { onActivateKey } from '../utils/keyboard';
import useDialog from '../hooks/useDialog';

// Icon name → component mapping for dynamic roles
const ICON_MAP = {
  Shield, Stethoscope, UserCog, Pill, UserCheck, Users, ClipboardList,
  Settings, Package, BarChart3, FileText, LayoutDashboard,
};

// Generate Tailwind classes from a color key (e.g. "red" → "from-red-500 to-rose-600")
const COLOR_STYLES = {
  red:     { color: 'from-red-500 to-rose-600', bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', ring: 'ring-red-400', accent: 'red' },
  blue:    { color: 'from-blue-500 to-indigo-600', bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700', ring: 'ring-blue-400', accent: 'blue' },
  purple:  { color: 'from-purple-500 to-violet-600', bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-700', ring: 'ring-purple-400', accent: 'purple' },
  emerald: { color: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700', ring: 'ring-emerald-400', accent: 'emerald' },
  amber:   { color: 'from-amber-500 to-orange-600', bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', ring: 'ring-amber-400', accent: 'amber' },
  gray:    { color: 'from-gray-500 to-gray-600', bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-700', ring: 'ring-gray-400', accent: 'gray' },
  teal:    { color: 'from-teal-500 to-cyan-600', bg: 'bg-teal-50', border: 'border-teal-300', text: 'text-teal-700', ring: 'ring-teal-400', accent: 'teal' },
  pink:    { color: 'from-pink-500 to-rose-600', bg: 'bg-pink-50', border: 'border-pink-300', text: 'text-pink-700', ring: 'ring-pink-400', accent: 'pink' },
  indigo:  { color: 'from-indigo-500 to-blue-600', bg: 'bg-indigo-50', border: 'border-indigo-300', text: 'text-indigo-700', ring: 'ring-indigo-400', accent: 'indigo' },
};

const getColorStyle = (colorKey) => COLOR_STYLES[colorKey] || COLOR_STYLES.gray;
const getIcon = (iconName) => ICON_MAP[iconName] || UserCog;

// Exported so the per-user permission-override modal (in UserManagement.js) can render
// the same catalog-driven structure as this page — keeps the two surfaces in lockstep.
export const MODULE_CONFIG = {
  DASHBOARD: { label: 'Dashboard', icon: LayoutDashboard },
  PATIENTS: { label: 'Patients', icon: Users },
  FAMILY: { label: 'Family Management', icon: Users },
  QUEUE_MANAGEMENT: { label: 'Queue Management', icon: Ticket },
  NURSE_STATION: { label: 'Nurse Station', icon: Activity },
  PRESCRIPTIONS: { label: 'Prescriptions', icon: FileText },
  INVOICE: { label: 'Invoice', icon: FileText },
  CASH_DRAWERS: { label: 'Cash Drawers', icon: Wallet },
  ATTENDANCE: { label: 'Attendance', icon: Clock },
  COMPLAINTS: { label: 'Complaints', icon: MessageSquare },
  EXPENSES: { label: 'Expenses', icon: BarChart3 },
  PHARMACY_MEDICINES: { label: 'Medicines', icon: PillIcon },
  PHARMACY_ORDERS: { label: 'Medicine Orders', icon: Boxes },
  PHARMACY_STOCK_RECEIPTS: { label: 'Stock Receipts', icon: ClipboardCheck },
  PHARMACY_ASSETS: { label: 'Assets & Equipment', icon: Package },
  PHARMACY_VENDORS: { label: 'Vendors', icon: Building2 },
  USER_MANAGEMENT: { label: 'User Management', icon: UserCog },
  REPORTS: { label: 'Reports', icon: BarChart3 },
  SETTINGS_MEDICAL_SERVICES: { label: 'Medical Services', icon: Stethoscope },
  SETTINGS_TAGS: { label: 'Tags', icon: Tag },
  SETTINGS_CLINICAL: { label: 'Clinical Settings', icon: HeartPulse },
  SETTINGS_TIMEZONE: { label: 'Timezone Settings', icon: Clock },
  SETTINGS_BRANDING: { label: 'Branding', icon: Palette },
};

// Collapsible section layout for the permission matrix. Mirrors the sidebar menu → submenu
// hierarchy: each entry is a top-level menu, and `modules` lists the permission module(s)
// it owns. Most menus map to a single module; the two parents with real submenus —
// Inventory (Pharmacy) and Settings — own several. Rendered as expandable sections so the
// page collapses to a short scannable list instead of one long flat wall of toggles.
// Exported so the per-user override modal in UserManagement.js renders the same grouping.
export const MODULE_GROUPS = [
  { id: 'dashboard',  label: 'Dashboard',          icon: LayoutDashboard, modules: ['DASHBOARD'] },
  { id: 'patients',   label: 'Patient',            icon: Users,           modules: ['PATIENTS'] },
  { id: 'nurse',      label: 'Nurse Station',      icon: Activity,        modules: ['NURSE_STATION'] },
  { id: 'queue',      label: 'Queue Management',   icon: Ticket,          modules: ['QUEUE_MANAGEMENT'] },
  { id: 'prescriptions', label: 'Prescription',    icon: FileText,        modules: ['PRESCRIPTIONS'] },
  { id: 'family',     label: 'Family Management',  icon: UserCog,         modules: ['FAMILY'] },
  { id: 'user-mgmt',  label: 'User Management',    icon: UserCog,         modules: ['USER_MANAGEMENT'] },
  { id: 'invoice',    label: 'Invoice',            icon: FileText,        modules: ['INVOICE', 'CASH_DRAWERS'] },
  { id: 'expenses',   label: 'Expenses',           icon: BarChart3,       modules: ['EXPENSES'] },
  { id: 'attendance', label: 'Attendance',         icon: Clock,           modules: ['ATTENDANCE'] },
  { id: 'complaints', label: 'Complaints',         icon: MessageSquare,   modules: ['COMPLAINTS'] },
  { id: 'inventory',  label: 'Inventory',          icon: Package,         modules: ['PHARMACY_MEDICINES', 'PHARMACY_ORDERS', 'PHARMACY_STOCK_RECEIPTS', 'PHARMACY_ASSETS', 'PHARMACY_VENDORS'] },
  { id: 'reports',    label: 'Reports',            icon: BarChart3,       modules: ['REPORTS'] },
  { id: 'settings',   label: 'Settings',           icon: Settings,        modules: ['SETTINGS_MEDICAL_SERVICES', 'SETTINGS_TAGS', 'SETTINGS_CLINICAL', 'SETTINGS_TIMEZONE', 'SETTINGS_BRANDING'] },
];

// Every action that can appear in the catalog needs an entry here. `group` controls how
// actions are visually grouped within a module that has more than the base 4 actions —
// 'base' (V/C/E/D), 'row' (row-level actions on a record), 'tab' (sub-views inside a
// detail modal). Modules using only base actions render as a single compact group.
export const ACTION_CONFIG = {
  VIEW:            { label: 'View',                  icon: Eye,         group: 'base' },
  CREATE:          { label: 'Create',                icon: Plus,        group: 'base' },
  EDIT:            { label: 'Edit',                  icon: Pencil,      group: 'base' },
  DELETE:          { label: 'Delete',                icon: Trash2,      group: 'base' },
  // Patients
  ISSUE_TOKEN:     { label: 'Issue token',           icon: TicketPlus,  group: 'row'  },
  UPLOAD_DOCUMENT: { label: 'Upload document',       icon: Upload,      group: 'row'  },
  VIEW_DOCUMENTS_TAB:       { label: 'Documents tab',        icon: FolderOpen,    group: 'tab' },
  VIEW_VISITS_TAB:          { label: 'Visits tab',           icon: Calendar,      group: 'tab' },
  VIEW_VITALS_TAB:          { label: 'Vitals tab',           icon: HeartPulse,    group: 'tab' },
  VIEW_CLINICAL_NOTES_TAB:  { label: 'Clinical Notes tab',   icon: StickyNote,    group: 'tab' },
  VIEW_PRESCRIPTIONS_TAB:   { label: 'Prescriptions tab',    icon: ScrollText,    group: 'tab' },
  VIEW_MEDICINES_TAB:       { label: 'Medicines tab',        icon: PillIcon,      group: 'tab' },
  VIEW_LAB_TESTS_TAB:       { label: 'Lab Tests tab',        icon: FlaskConical,  group: 'tab' },
  VIEW_RADIOLOGY_TAB:       { label: 'Radiology tab',        icon: ImageIcon,     group: 'tab' },
  VIEW_MEDICAL_HISTORY_TAB: { label: 'Medical History tab',  icon: BookOpen,      group: 'tab' },
  VIEW_INVOICES_TAB:        { label: 'Invoices tab',         icon: Wallet,        group: 'tab' },
  VIEW_REPORT_NOTES_TAB:    { label: 'Report Notes tab',     icon: NotebookPen,   group: 'tab' },
  // Family
  LINK_MEMBER:     { label: 'Link family member',    icon: Link2,       group: 'row'  },
  UNLINK_MEMBER:   { label: 'Unlink family member',  icon: Unlink,      group: 'row'  },
  // Queue / Nurse / Prescriptions — workflow transitions
  SETTLE_REFERRAL: { label: 'Settle a referral',     icon: Banknote,    group: 'row'  },
  REFER_PATIENT:   { label: 'Refer a patient',       icon: Share2,      group: 'row'  },
  CALL_TOKEN:      { label: 'Call token',            icon: PhoneCall,   group: 'row'  },
  RECALL_TOKEN:    { label: 'Recall token',          icon: PhoneOutgoing, group: 'row' },
  REPRINT_TOKEN:   { label: 'Reprint token',         icon: Printer,     group: 'row'  },
  DEFER_VISIT:     { label: 'Defer visit',           icon: PauseCircle, group: 'row'  },
  COMPLETE_VISIT:  { label: 'Complete visit',        icon: CheckCircle, group: 'row'  },
  CALL_FOR_VITALS: { label: 'Call for vitals',       icon: PhoneCall,   group: 'row'  },
  RECORD_VITALS:   { label: 'Record vitals',         icon: HeartPulse,  group: 'row'  },
  VIEW_PENDING_TAB:  { label: 'Pending tab',         icon: ListChecks,  group: 'tab'  },
  VIEW_RECORDED_TAB: { label: 'Recorded tab',        icon: ClipboardCheck, group: 'tab' },
  VIEW_INJECTIONS_TAB: { label: 'Injections tab',    icon: Syringe,     group: 'tab'  },
  RECORD_DOSE:       { label: 'Record doses',        icon: Syringe,     group: 'row'  },
  VIEW_QUEUE_TAB:    { label: 'Queue tab',           icon: Ticket,      group: 'tab'  },
  VIEW_TOKEN_QUEUE_TAB:    { label: 'Token Queue tab',    icon: ListOrdered, group: 'tab' },
  VIEW_TOKEN_RECEIPTS_TAB: { label: 'Token Receipts tab', icon: Receipt,     group: 'tab' },
  VIEW_DISPLAY_BOARD:      { label: 'Display Board link',  icon: Monitor,     group: 'tab' },
  // Reports — one toggle per sidebar group. The clinical group exposes diagnoses and the OPD
  // group exposes patient identifiers, so they are grantable separately from finance.
  VIEW_FINANCE_REPORTS:   { label: 'Finance reports',   icon: Banknote,    group: 'tab' },
  VIEW_OPD_REPORTS:       { label: 'OPD reports',       icon: Users,       group: 'tab' },
  VIEW_CLINICAL_REPORTS:  { label: 'Clinical reports',  icon: Stethoscope, group: 'tab' },
  VIEW_INVENTORY_REPORTS: { label: 'Inventory reports', icon: Boxes,       group: 'tab' },
  // Lists every patient on opening, unlike every other patient list, so it is granted on its own.
  VIEW_PATIENT_REGISTER:  { label: 'Patient register',  icon: Users,       group: 'tab' },
  VIEW_ATTENDANCE_REPORTS:{ label: 'Attendance reports', icon: Clock,      group: 'tab' },
  // Two attendance reports put a member of staff on a map. Knowing somebody was late is one thing;
  // knowing where they were standing is another, so it is granted on top of the group.
  VIEW_STAFF_LOCATIONS:   { label: 'Where staff punched from', icon: MapPin, group: 'tab' },
  // Payroll shows every salary, so it is granted separately from the Finance group.
  VIEW_PAYROLL:           { label: 'Payroll',           icon: Banknote,    group: 'tab' },
  VIEW_HISTORY_TAB:  { label: 'History tab',         icon: BookOpen,    group: 'tab'  },
  DELETE_TEMPLATE:   { label: 'Delete template',     icon: Trash2,      group: 'row'  },
  // Invoice
  FINALIZE_INVOICE: { label: 'Finalize invoice',     icon: CheckCircle, group: 'row'  },
  RECORD_PAYMENT:   { label: 'Record payment',       icon: CreditCard,  group: 'row'  },
  CANCEL_INVOICE:   { label: 'Cancel invoice',       icon: XCircle,     group: 'row'  },
  PRINT_INVOICE:    { label: 'Print invoice',        icon: Printer,     group: 'row'  },
  APPLY_DISCOUNT:   { label: 'Apply discount',       icon: Percent,     group: 'row'  },
  // Invoice tabs (Token Receipts reuses the Queue Management action above).
  VIEW_COUNTER_TAB:      { label: 'Counter tab',      icon: Banknote,   group: 'tab'  },
  VIEW_ALL_INVOICES_TAB: { label: 'All invoices tab', icon: Receipt,    group: 'tab'  },
  // Cash drawers. Seeing every drawer is the module's VIEW; a cashier's own drawer needs neither.
  CLOSE_ANY_DRAWER: { label: "Close another cashier's drawer", icon: Banknote, group: 'row' },
  // Who works a drawer of their own, and so sees it in the top bar.
  OPERATE_DRAWER:   { label: 'Works a cash drawer', icon: Wallet, group: 'row' },
  // Attendance. VIEW is a person's own page and punching; the rest is everyone's attendance.
  VIEW_ALL:         { label: "See everyone's attendance", icon: Eye,      group: 'row' },
  CORRECT_PUNCH:    { label: 'Correct punch times',       icon: Edit2,    group: 'row' },
  REVIEW_FLAGGED:   { label: 'Review flagged punches',    icon: CheckCircle, group: 'row' },
  APPROVE_LEAVE:    { label: 'Decide leave requests',     icon: CalendarCheck, group: 'row' },
  MANAGE_HOLIDAYS:  { label: 'Set the holidays',          icon: CalendarDays,  group: 'row' },
  // Expenses tabs (RECORD_PAYMENT shared above)
  VIEW_DASHBOARD_TAB:  { label: 'Dashboard tab',     icon: LayoutGrid,  group: 'tab'  },
  VIEW_ENTRIES_TAB:    { label: 'Entries tab',       icon: FileText,    group: 'tab'  },
  VIEW_CATEGORIES_TAB: { label: 'Categories tab',    icon: FolderOpen,  group: 'tab'  },
  VIEW_CHEQUES_TAB:    { label: 'Cheques tab',       icon: Wallet,      group: 'tab'  },
  // Pharmacy — Medicines
  UPDATE_STOCK:       { label: 'Update stock',       icon: PackagePlus, group: 'row'  },
  ADJUST_INVENTORY:   { label: 'Adjust inventory',   icon: RotateCw,    group: 'row'  },
  // Pharmacy — Orders
  SUBMIT_ORDER:       { label: 'Submit order',       icon: PackageCheck, group: 'row' },
  RECEIVE_ORDER:      { label: 'Receive order',      icon: PackageCheck, group: 'row' },
  CANCEL_ORDER:       { label: 'Cancel order',       icon: PackageX,    group: 'row'  },
  // Pharmacy — Stock receipts
  CANCEL_RECEIPT:     { label: 'Cancel receipt',     icon: RotateCcw,   group: 'row'  },
  // Pharmacy — Assets
  SCHEDULE_MAINTENANCE: { label: 'Schedule maintenance', icon: Wrench,  group: 'row'  },
  RETIRE_ASSET:       { label: 'Retire asset',       icon: ArchiveX,    group: 'row'  },
  // Pharmacy — Vendors
  SUSPEND_VENDOR:     { label: 'Suspend vendor',     icon: Ban,         group: 'row'  },
  // User Management
  RESET_PASSWORD:     { label: 'Reset password',     icon: KeyRound,    group: 'row'  },
  SUSPEND_USER:       { label: 'Suspend user',       icon: UserX,       group: 'row'  },
  MANAGE_ROLES:       { label: 'Manage roles',       icon: Shield,      group: 'row'  },
  MANAGE_PERMISSIONS: { label: 'Manage permissions', icon: ShieldCheck, group: 'row'  },
  VIEW_USERS_TAB:     { label: 'Users tab',          icon: Users,       group: 'tab'  },
  VIEW_ROLES_TAB:     { label: 'Roles tab',          icon: Shield,      group: 'tab'  },
  // Complaints. VIEW is every complaint; being assigned one shows that one and no others, which
  // needs no permission at all, so there is nothing here for it.
  ASSIGN_COMPLAINT:   { label: 'Assign a complaint', icon: UserCheck,   group: 'row'  },
  // Settings — Medical Services
  TOGGLE_ACTIVE:      { label: 'Toggle active',      icon: ToggleLeft,  group: 'row'  },
};

/**
 * What to show for an action the catalog sent that ACTION_CONFIG has no entry for.
 *
 * Both permission screens used to return null here, which meant a real, enforced permission simply
 * did not appear: whoever the seed had granted it kept it, and no administrator could grant or
 * revoke it. SETTINGS_BRANDING was invisible that way for months in 2026, and an audit on
 * 2026-10-03 found five more (SETTLE_REFERRAL, REFER_PATIENT, APPROVE_LEAVE, MANAGE_HOLIDAYS,
 * MANAGE_SCHEDULES). The lists below are hand-maintained and the server is free to add an action
 * without them, so the screens now draw an unknown action with its name spelled out rather than
 * hiding it. A switch that looks plain is a prompt to add a proper entry; one that is not there at
 * all is a permission nobody can reach.
 */
export const fallbackActionConfig = (action) => ({
  label: String(action)
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase()),
  icon: Shield,
  group: 'row',
});

export const GROUP_LABEL = {
  base: 'Basic',
  row:  'Row actions',
  tab:  'Detail tabs',
};

export const BASE_ACTIONS = ['VIEW', 'CREATE', 'EDIT', 'DELETE'];

const RolesPermissions = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('ADMIN');
  const [permissions, setPermissions] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState({});
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // "Who holds this role" — opened from a card's user count. Fetched on demand rather than bundled
  // into the summary, so the screen's own load stays as light as it was.
  const [usersForRole, setUsersForRole] = useState(null); // { role, users } | null
  const [usersLoading, setUsersLoading] = useState(false);
  // Per-module action catalog — fetched once. Falls back to BASE_ACTIONS for any module not in
  // the response (defensive against backend/frontend skew during a partial deploy).
  const [catalog, setCatalog] = useState({});
  // Which collapsible menu sections are expanded. Start collapsed so the page opens as a
  // short scannable list; the user expands the menus they want to edit.
  const [expandedGroups, setExpandedGroups] = useState([]);

  // Build dynamic ROLE_CONFIG from fetched roles
  const ROLE_CONFIG = {};
  roles.forEach(r => {
    const style = getColorStyle(r.color);
    ROLE_CONFIG[r.name] = { label: r.label, icon: getIcon(r.icon), description: r.description, isSystem: r.isSystem, ...style };
  });

  // Delegates to the shared UIProvider toast. Kept as a useCallback (and (message, type)
  // wrapper) so existing call sites and [showToast] dependency arrays stay unchanged;
  // the hook's signature is (type, message).
  const notify = useToast();
  const showToast = useCallback((message, type = 'success') => notify(type, message), [notify]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [allPerms, summaryData, rolesData, catalogData] = await Promise.all([
        rolePermissionAPI.getAll(),
        rolePermissionAPI.getSummary(),
        rolePermissionAPI.getRoles(),
        rolePermissionAPI.getCatalog().catch(() => ({})), // tolerate older backends
      ]);
      setPermissions(allPerms);
      setOriginalPermissions(JSON.parse(JSON.stringify(allPerms)));
      setSummary(summaryData);
      setRoles(Array.isArray(rolesData) ? rolesData : []);
      setCatalog(catalogData && typeof catalogData === 'object' ? catalogData : {});
    } catch (err) {
      showToast('Failed to load permissions: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Lookup helper — returns the action list for a module, falling back to base 4 if the
  // catalog hasn't loaded or doesn't list this module.
  const actionsFor = useCallback(
    (moduleName) => {
      const fromCatalog = catalog[moduleName];
      const actions =
        Array.isArray(fromCatalog) && fromCatalog.length > 0 ? fromCatalog : BASE_ACTIONS;
      // Drop toggles whose feature is parked, so no one can grant a permission for a screen that
      // isn't reachable. Filtering here covers rendering and the grant/deny-all sweeps alike.
      return actions.filter(a => !HIDDEN_PERMISSION_ACTIONS.includes(a));
    },
    [catalog],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Switching roles swaps the long permission grid in place — reset the page scroll so the
  // new role's grid starts at the top instead of inheriting the previous role's position.
  useEffect(() => {
    scrollContentToTop();
  }, [selectedRole]);

  // Check for unsaved changes
  useEffect(() => {
    const currentRole = permissions[selectedRole]?.permissions;
    const originalRole = originalPermissions[selectedRole]?.permissions;
    if (!currentRole || !originalRole) {
      setHasChanges(false);
      return;
    }
    setHasChanges(JSON.stringify(currentRole) !== JSON.stringify(originalRole));
  }, [permissions, originalPermissions, selectedRole]);

  const resetConfirmDialogRef = useDialog(() => setShowResetConfirm(false), { confirmDiscard: false });
  const unsavedConfirmDialogRef = useDialog(() => setShowUnsavedConfirm(null), { confirmDiscard: false });
  const usersForRoleDialogRef = useDialog(() => setUsersForRole(null), { confirmDiscard: false });

  const handleToggle = (module, action) => {
    setPermissions(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const rolePerms = updated[selectedRole]?.permissions;
      if (!rolePerms) return updated;
      // Initialize the module with all-false using the module's actual action set if it doesn't
      // exist yet (e.g., new sub-actions added after the role was last seeded — backend will
      // create the rows on next save/reset).
      if (!rolePerms[module]) {
        rolePerms[module] = Object.fromEntries(actionsFor(module).map(a => [a, false]));
      }
      rolePerms[module][action] = !rolePerms[module][action];
      return updated;
    });
  };

  // Set every action in a module to `newVal` in one batch — used by the per-module
  // master switch. Keeps the toggle responsive (single React update vs N toggle calls).
  const handleBulkToggle = (module, newVal) => {
    setPermissions(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const rolePerms = updated[selectedRole]?.permissions;
      if (!rolePerms) return updated;
      if (!rolePerms[module]) rolePerms[module] = {};
      actionsFor(module).forEach(a => { rolePerms[module][a] = newVal; });
      return updated;
    });
  };

  // Grant/deny every action across every (visible) module in a menu section in one batch.
  const handleGroupBulkToggle = (group, newVal) => {
    setPermissions(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const rolePerms = updated[selectedRole]?.permissions;
      if (!rolePerms) return updated;
      group.modules
        .filter(m => !HIDDEN_PERMISSION_MODULES.includes(m))
        .forEach(m => {
          if (!rolePerms[m]) rolePerms[m] = {};
          actionsFor(m).forEach(a => { rolePerms[m][a] = newVal; });
        });
      return updated;
    });
  };

  const toggleGroup = (id) =>
    setExpandedGroups(prev => (prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]));
  const expandAllGroups = () => setExpandedGroups(MODULE_GROUPS.map(g => g.id));
  const collapseAllGroups = () => setExpandedGroups([]);

  // Whole-role master switch — grant or deny EVERY action across EVERY visible module in one
  // batch. Mirrors handleGroupBulkToggle but spans all sections (the top-level equivalent of
  // the per-module/per-section switches).
  const handleAllToggle = (newVal) => {
    setPermissions(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const rolePerms = updated[selectedRole]?.permissions;
      if (!rolePerms) return updated;
      MODULE_GROUPS.forEach(group =>
        group.modules
          .filter(m => !HIDDEN_PERMISSION_MODULES.includes(m))
          .forEach(m => {
            if (!rolePerms[m]) rolePerms[m] = {};
            actionsFor(m).forEach(a => { rolePerms[m][a] = newVal; });
          }));
      return updated;
    });
  };

  // True only when every visible action of every module is granted for the selected role —
  // drives the header master switch's on/off state.
  const allPermissionsGranted = (() => {
    const rolePerms = permissions[selectedRole]?.permissions || {};
    let any = false, all = true;
    MODULE_GROUPS.forEach(group =>
      group.modules
        .filter(m => !HIDDEN_PERMISSION_MODULES.includes(m))
        .forEach(m => actionsFor(m).forEach(a => {
          any = true;
          if (!rolePerms[m]?.[a]) all = false;
        })));
    return any && all;
  })();

  const handleSave = async () => {
    setSaving(true);
    try {
      const rolePerms = permissions[selectedRole]?.permissions;
      await rolePermissionAPI.updateRole(selectedRole, rolePerms);
      setOriginalPermissions(JSON.parse(JSON.stringify(permissions)));
      // Refresh summary
      const summaryData = await rolePermissionAPI.getSummary();
      setSummary(summaryData);
      showToast(`Permissions saved for ${ROLE_CONFIG[selectedRole].label}`);
      // Refresh app permissions if the current user's role was modified
      window.dispatchEvent(new Event('permissions:changed'));
    } catch (err) {
      showToast('Failed to save: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setShowResetConfirm(false);
    setSaving(true);
    try {
      const result = await rolePermissionAPI.resetToDefaults(selectedRole);
      setPermissions(prev => ({
        ...prev,
        [selectedRole]: result,
      }));
      setOriginalPermissions(prev => ({
        ...prev,
        [selectedRole]: JSON.parse(JSON.stringify(result)),
      }));
      const summaryData = await rolePermissionAPI.getSummary();
      setSummary(summaryData);
      showToast(`Permissions reset to defaults for ${ROLE_CONFIG[selectedRole].label}`);
    } catch (err) {
      showToast('Failed to reset: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRoleSwitch = (role) => {
    if (role === selectedRole) return;
    if (hasChanges) {
      setShowUnsavedConfirm(role);
    } else {
      setSelectedRole(role);
    }
  };

  const confirmRoleSwitch = (save) => {
    const targetRole = showUnsavedConfirm;
    setShowUnsavedConfirm(null);
    if (save) {
      handleSave().then(() => setSelectedRole(targetRole));
    } else {
      // Discard changes
      setPermissions(prev => ({
        ...prev,
        [selectedRole]: JSON.parse(JSON.stringify(originalPermissions[selectedRole])),
      }));
      setSelectedRole(targetRole);
    }
  };

  const rolePerms = permissions[selectedRole]?.permissions || {};

  const getGrantedCount = (role) => {
    const s = summary.find(s => s.role === role);
    return s ? s.grantedPermissions : 0;
  };

  const getTotalCount = (role) => {
    const s = summary.find(s => s.role === role);
    return s ? s.totalPermissions : 0;
  };

  const getUserCount = (role) => {
    const s = summary.find(s => s.role === role);
    return s ? s.userCount : 0;
  };

  const openUsersForRole = async (role) => {
    setUsersForRole({ role, users: [] });
    setUsersLoading(true);
    try {
      const list = await rolePermissionAPI.getUsersForRole(role);
      setUsersForRole({ role, users: Array.isArray(list) ? list : [] });
    } catch (err) {
      setUsersForRole(null);
      showToast(err.message || 'Could not load the users for this role', 'error');
    } finally {
      setUsersLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header skeleton */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 animate-pulse">
            <div className="flex items-center space-x-5">
              <div className="w-16 h-16 bg-gray-200 rounded-2xl" />
              <div className="space-y-2">
                <div className="w-48 h-7 bg-gray-200 rounded" />
                <div className="w-72 h-4 bg-gray-200 rounded" />
              </div>
            </div>
          </div>
          {/* Cards skeleton */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="bg-white rounded-2xl shadow-xl p-5 animate-pulse">
                <div className="w-10 h-10 bg-gray-200 rounded-xl mb-3" />
                <div className="w-20 h-5 bg-gray-200 rounded mb-2" />
                <div className="w-16 h-4 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* ==================== HEADER ==================== */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-5">
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
                  <Shield className="h-8 w-8 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Roles & Permissions
                </h1>
                <p className="text-gray-500 mt-1">Configure access control for each role across system modules</p>
              </div>
            </div>
            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-5 py-2.5 font-medium inline-flex items-center gap-2 disabled:opacity-50"
              >
                <Save className="h-5 w-5" />
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            )}
          </div>
        </div>

        {/* ==================== ROLE CARDS ==================== */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {Object.entries(ROLE_CONFIG).map(([role, config]) => {
            const IconComponent = config.icon;
            const isSelected = selectedRole === role;
            return (
              // A div rather than a button: the user count inside is itself a button, and a button
              // cannot legally contain another. Keyboard behaviour is restored explicitly.
              <div
                key={role}
                role="button"
                tabIndex={0}
                onClick={() => handleRoleSwitch(role)}
                onKeyDown={onActivateKey(() => handleRoleSwitch(role))}
                className={`
                  group relative bg-white rounded-2xl shadow-xl p-5 border-2 transition-all duration-300 cursor-pointer text-left
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2
                  ${isSelected
                    ? `${config.border} ring-2 ${config.ring} ring-offset-2`
                    : 'border-gray-100 hover:-translate-y-0.5'
                  }
                `}
              >
                {/* Color bar */}
                <div className={`absolute top-0 left-0 right-0 h-1.5 rounded-t-2xl bg-gradient-to-r ${config.color}`} />

                <div className="flex flex-col items-center text-center pt-2">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${isSelected ? config.bg : 'bg-gray-100 group-hover:' + config.bg}`}>
                    <IconComponent className={`h-6 w-6 ${isSelected ? config.text : 'text-gray-500 group-hover:' + config.text}`} />
                  </div>
                  <h3 className={`font-bold text-sm ${isSelected ? config.text : 'text-gray-700'}`}>
                    {config.label}
                  </h3>
                  {/* The count opens the list of people who hold the role. Selecting the role and
                      asking who is in it are different questions, so this does not switch tabs. */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openUsersForRole(role); }}
                    disabled={getUserCount(role) === 0}
                    title={getUserCount(role) === 0 ? 'Nobody holds this role' : `See who holds the ${config.label} role`}
                    className={`flex items-center space-x-1 mt-1.5 px-2 py-0.5 -mx-2 rounded-md transition-colors ${
                      getUserCount(role) === 0
                        ? 'cursor-default'
                        : 'hover:bg-gray-100 hover:text-gray-700 cursor-pointer'
                    }`}
                  >
                    <Users className="w-3 h-3 text-gray-400" />
                    <span className={`text-xs ${getUserCount(role) === 0 ? 'text-gray-400' : 'text-gray-500 underline decoration-dotted underline-offset-2'}`}>
                      {getUserCount(role)} {getUserCount(role) === 1 ? 'user' : 'users'}
                    </span>
                  </button>
                  <div className="mt-1">
                    <span className={`text-xs font-medium ${isSelected ? config.text : 'text-gray-500'}`}>
                      {getGrantedCount(role)}/{getTotalCount(role)} permissions
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          {/* Add Role button */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-white rounded-2xl shadow-xl p-5 border-2 border-dashed border-gray-300 hover:border-emerald-400 hover:bg-emerald-50/50 transition-all flex flex-col items-center justify-center min-w-[140px]"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-gray-100">
              <Plus className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="font-bold text-sm text-gray-500">Add Role</h3>
          </button>
        </div>

        {/* ==================== PERMISSION GRID ==================== */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Grid Header */}
          <div className={`bg-gradient-to-r ${ROLE_CONFIG[selectedRole].color} p-6`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {React.createElement(ROLE_CONFIG[selectedRole].icon, { className: 'h-6 w-6 text-white' })}
                <h2 className="text-xl font-bold text-white">
                  {ROLE_CONFIG[selectedRole].label} Permissions
                </h2>
              </div>
              <div className="flex items-center space-x-2">
                {/* Whole-role master switch — grant/deny every permission in every module.
                    Label shows the action a click will perform so it reads like a clear
                    "open all / close all" control. */}
                <div className="flex items-center space-x-2 px-3 py-2 bg-white/20 rounded-xl">
                  <span className="hidden md:inline text-white text-sm font-medium">
                    {allPermissionsGranted ? 'Deny all' : 'Grant all'}
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={allPermissionsGranted}
                    onClick={() => handleAllToggle(!allPermissionsGranted)}
                    title={allPermissionsGranted ? 'Deny all permissions' : 'Grant all permissions'}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent flex-shrink-0 ${allPermissionsGranted ? 'bg-white' : 'bg-white/30'}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full shadow-sm transition-transform duration-200 ${allPermissionsGranted ? `translate-x-5 bg-gradient-to-r ${ROLE_CONFIG[selectedRole].color}` : 'translate-x-0.5 bg-white'}`} />
                  </button>
                </div>
                <button
                  onClick={expandAllGroups}
                  className="flex items-center space-x-2 px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl text-sm font-medium transition-colors"
                  title="Expand all sections"
                >
                  <ChevronsUpDown className="w-4 h-4" />
                  <span className="hidden sm:inline">Expand all</span>
                </button>
                <button
                  onClick={collapseAllGroups}
                  className="flex items-center space-x-2 px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl text-sm font-medium transition-colors"
                  title="Collapse all sections"
                >
                  <ChevronsDownUp className="w-4 h-4" />
                  <span className="hidden sm:inline">Collapse all</span>
                </button>
                <button
                  onClick={() => setShowResetConfirm(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl text-sm font-medium transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span className="hidden sm:inline">Reset to Defaults</span>
                </button>
              </div>
            </div>
          </div>

          {/* Permission sections — one collapsible card per top-level menu, mirroring the
              sidebar's menu → submenu hierarchy. Single-module menus show their action
              toggles directly when expanded; multi-module parents (Inventory, Settings)
              show a labelled sub-block with its own master switch per submodule. */}
          <div className="p-4 sm:p-5 space-y-3">
            {MODULE_GROUPS.map((group) => {
              const GroupIcon = group.icon;
              const isOpen = expandedGroups.includes(group.id);
              // Drop modules hidden behind a parked feature flag; skip the whole section if
              // nothing is left to show (see config/features.js).
              const modules = group.modules.filter(m => !HIDDEN_PERMISSION_MODULES.includes(m));
              if (modules.length === 0) return null;
              const isMulti = modules.length > 1;

              // Section-wide granted tally across every action of every module it owns.
              let grantedCount = 0, totalCount = 0;
              modules.forEach(m => {
                const mp = rolePerms[m] || {};
                actionsFor(m).forEach(a => { totalCount++; if (mp[a]) grantedCount++; });
              });
              const groupAllGranted = totalCount > 0 && grantedCount === totalCount;

              return (
                <div key={group.id} className="border border-gray-200 rounded-2xl bg-white overflow-hidden">
                  {/* Section header — click anywhere on the title to expand/collapse. */}
                  <div className={`flex items-center gap-3 px-4 py-3 transition-colors ${isOpen ? 'bg-gray-50 border-b border-gray-100' : 'hover:bg-gray-50'}`}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.id)}
                      className="flex items-center gap-3 flex-1 min-w-0 text-left"
                      aria-expanded={isOpen}
                    >
                      {isOpen
                        ? <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        : <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />}
                      <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <GroupIcon className="w-5 h-5 text-gray-600" />
                      </div>
                      <span className="font-semibold text-gray-800 truncate">{group.label}</span>
                      {isMulti && <span className="text-xs font-medium text-gray-400 flex-shrink-0">{modules.length} items</span>}
                    </button>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${grantedCount > 0 ? `${ROLE_CONFIG[selectedRole].bg} ${ROLE_CONFIG[selectedRole].text}` : 'bg-gray-100 text-gray-400'}`}>
                      {grantedCount}/{totalCount}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleGroupBulkToggle(group, !groupAllGranted)}
                      title={groupAllGranted ? `Deny all in ${group.label}` : `Grant all in ${group.label}`}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 flex-shrink-0 ${groupAllGranted ? `bg-gradient-to-r ${ROLE_CONFIG[selectedRole].color}` : 'bg-gray-300'}`}
                      role="switch"
                      aria-checked={groupAllGranted}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${groupAllGranted ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>

                  {/* Section body — module action toggles, shown only when expanded. */}
                  {isOpen && (
                    <div className="divide-y divide-gray-100">
                      {modules.map((module) => {
                        const modConf = MODULE_CONFIG[module];
                        const ModIcon = modConf.icon;
                        const modulePerms = rolePerms[module] || {};
                        const modActions = actionsFor(module);

                        // Group action keys by `group` so the renderer can lay out sections.
                        const byGroup = { base: [], row: [], tab: [] };
                        for (const a of modActions) {
                          const g = ACTION_CONFIG[a]?.group || 'base';
                          if (byGroup[g]) byGroup[g].push(a);
                        }
                        const hasExtras = byGroup.row.length > 0 || byGroup.tab.length > 0;

                        const renderToggle = (action) => {
                          const conf = ACTION_CONFIG[action] || fallbackActionConfig(action);
                          const ActionIcon = conf.icon;
                          const isGranted = modulePerms[action] ?? false;
                          return (
                            <label
                              key={action}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${isGranted ? `${ROLE_CONFIG[selectedRole].bg} ${ROLE_CONFIG[selectedRole].border}` : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                            >
                              <button
                                type="button"
                                onClick={() => handleToggle(module, action)}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 flex-shrink-0 ${isGranted ? `bg-gradient-to-r ${ROLE_CONFIG[selectedRole].color}` : 'bg-gray-300'}`}
                                role="switch"
                                aria-checked={isGranted}
                              >
                                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${isGranted ? 'translate-x-5' : 'translate-x-0.5'}`} />
                              </button>
                              <ActionIcon className={`w-4 h-4 ${isGranted ? ROLE_CONFIG[selectedRole].text : 'text-gray-400'}`} />
                              <span className={`text-sm font-medium ${isGranted ? ROLE_CONFIG[selectedRole].text : 'text-gray-700'}`}>{conf.label}</span>
                            </label>
                          );
                        };

                        const renderActionGroup = (groupKey) => {
                          const actions = byGroup[groupKey];
                          if (!actions || actions.length === 0) return null;
                          return (
                            <div key={groupKey} className="space-y-2">
                              {hasExtras && (
                                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                                  {GROUP_LABEL[groupKey]}
                                </div>
                              )}
                              <div className="flex flex-wrap gap-2">
                                {actions.map(renderToggle)}
                              </div>
                            </div>
                          );
                        };

                        // Per-module master toggle — only rendered for multi-module parents,
                        // since for single-module sections the section header already covers it.
                        const allGranted = modActions.length > 0 && modActions.every(a => modulePerms[a]);

                        return (
                          <div key={module} className="px-4 sm:px-6 py-4 hover:bg-emerald-50/20 transition-colors">
                            {isMulti && (
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <ModIcon className="w-4 h-4 text-gray-500" />
                                </div>
                                <span className="font-medium text-gray-700 flex-1 leading-tight break-words" title={modConf.label}>{modConf.label}</span>
                                <button
                                  type="button"
                                  onClick={() => handleBulkToggle(module, !allGranted)}
                                  title={allGranted ? 'Deny all in this item' : 'Grant all in this item'}
                                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 flex-shrink-0 ${allGranted ? `bg-gradient-to-r ${ROLE_CONFIG[selectedRole].color}` : 'bg-gray-300'}`}
                                  role="switch"
                                  aria-checked={allGranted}
                                >
                                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${allGranted ? 'translate-x-5' : 'translate-x-0.5'}`} />
                                </button>
                              </div>
                            )}
                            <div className="space-y-3">
                              {renderActionGroup('base')}
                              {renderActionGroup('row')}
                              {renderActionGroup('tab')}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer with legend */}
          <div className="px-6 py-4 bg-gray-50/80 border-t border-gray-100 flex items-center justify-between">
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <div className="flex items-center space-x-1.5">
                <div className={`w-8 h-4 rounded-full bg-gradient-to-r ${ROLE_CONFIG[selectedRole].color}`} />
                <span>Granted</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <div className="w-8 h-4 rounded-full bg-gray-300" />
                <span>Denied</span>
              </div>
            </div>
            {hasChanges && (
              <div className="flex items-center space-x-2 text-amber-600 text-sm">
                <AlertTriangle className="w-4 h-4" />
                <span className="font-medium">You have unsaved changes</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ==================== RESET CONFIRM MODAL ==================== */}
      {showResetConfirm && (
        <div ref={resetConfirmDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className={`bg-gradient-to-r ${ROLE_CONFIG[selectedRole].color} p-6`}>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <RotateCcw className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Reset Permissions</h3>
                  <p className="text-white/80 text-sm">This will reset to system defaults</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600">
                Are you sure you want to reset all permissions for <strong>{ROLE_CONFIG[selectedRole].label}</strong> to their default values? This cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-2xl">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-5 py-2.5 text-gray-700 hover:bg-gray-200 rounded-xl font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                className={`px-5 py-2.5 bg-gradient-to-r ${ROLE_CONFIG[selectedRole].color} text-white rounded-xl font-semibold hover:shadow-lg transition-all`}
              >
                Reset to Defaults
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== UNSAVED CHANGES MODAL ==================== */}
      {showUnsavedConfirm && (
        <div ref={unsavedConfirmDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Unsaved Changes</h3>
                  <p className="text-white/80 text-sm">You have unsaved permission changes</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600">
                You have unsaved changes for <strong>{ROLE_CONFIG[selectedRole].label}</strong>. Would you like to save them before switching roles?
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-2xl">
              <button
                onClick={() => setShowUnsavedConfirm(null)}
                className="px-5 py-2.5 text-gray-700 hover:bg-gray-200 rounded-xl font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmRoleSwitch(false)}
                className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-semibold transition-colors"
              >
                Discard
              </button>
              <button
                onClick={() => confirmRoleSwitch(true)}
                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold transition-all"
              >
                Save & Switch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== USERS IN THIS ROLE ==================== */}
      {usersForRole && (
        <div
          ref={usersForRoleDialogRef}
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setUsersForRole(null); }}
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {(ROLE_CONFIG[usersForRole.role]?.label) || usersForRole.role}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {usersLoading
                    ? 'Loading the people who hold this role'
                    : `${usersForRole.users.length} ${usersForRole.users.length === 1 ? 'person holds' : 'people hold'} this role`}
                </p>
              </div>
              <button
                onClick={() => setUsersForRole(null)}
                className="p-2 hover:bg-gray-100 rounded-lg flex-none"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {usersLoading ? (
                <div className="flex justify-center py-10">
                  <div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
                </div>
              ) : usersForRole.users.length === 0 ? (
                <p className="text-center py-10 text-sm text-gray-400">Nobody holds this role yet.</p>
              ) : (
                <div className="border border-gray-200 rounded-xl divide-y divide-gray-100">
                  {usersForRole.users.map((u) => {
                    // An account can be switched off without losing its role, so say so plainly
                    // rather than let a disabled account read as someone who can still sign in.
                    const disabled = u.isActive === false || (u.status && u.status !== 'ACTIVE');
                    return (
                      <div key={u.id} className="flex items-start gap-3 px-4 py-3">
                        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-none text-xs font-semibold text-gray-600">
                          {(u.fullName || u.username || '?').trim().charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {u.fullName || u.username}
                            {u.speciality && (
                              <span className="ml-2 text-xs font-normal text-gray-500">{u.speciality}</span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {u.username}
                            {u.email && ` · ${u.email}`}
                            {u.phone && ` · ${u.phone}`}
                          </p>
                        </div>
                        {disabled && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800 flex-none">
                            {u.status && u.status !== 'ACTIVE' ? u.status.charAt(0) + u.status.slice(1).toLowerCase() : 'Inactive'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setUsersForRole(null)}
                className="px-5 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== CREATE ROLE MODAL ==================== */}
      {showCreateModal && (
        <CreateRoleModal
          existingRoles={roles}
          onClose={() => setShowCreateModal(false)}
          onCreated={(newRole) => {
            setShowCreateModal(false);
            loadData();
            setSelectedRole(newRole.name);
            showToast(`Role "${newRole.label}" created successfully`);
          }}
        />
      )}
    </div>
  );
};

// ==================== CREATE ROLE MODAL ====================
const COLOR_OPTIONS = ['red','blue','purple','emerald','amber','teal','pink','indigo','gray'];
const ICON_OPTIONS = ['Shield','Stethoscope','UserCog','Pill','UserCheck','Users','ClipboardList','Package','Settings','BarChart3'];

// Exported so the Create User flow in UserManagement can reuse the same form when the
// admin clicks "+ Add new role" from the role picker.
export const CreateRoleModal = ({ existingRoles, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', label: '', description: '', color: 'gray', icon: 'UserCog', copyFromRole: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const createRoleDialogRef = useDialog(onClose);

  const handleSubmit = async () => {
    if (!form.label.trim()) { setError('Role name is required'); return; }
    const name = form.name.trim() || form.label.toUpperCase().replace(/[^A-Z0-9]/gi, '_');
    setSaving(true);
    setError('');
    try {
      const result = await rolePermissionAPI.createRole({ ...form, name });
      if (result.error) { setError(result.error); return; }
      onCreated(result.role || { name, label: form.label });
    } catch (err) { setError(err.message || 'Failed to create role'); }
    finally { setSaving(false); }
  };

  return (
    <div ref={createRoleDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {/* No overflow-hidden on the shell — the Copy-Permissions dropdown inside needs to be able to
          extend past it. Corners are rounded on the header and footer instead. */}
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-lg font-bold text-white">Create New Role</h2>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg"><X className="h-5 w-5 text-white" /></button>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm px-4 py-2 rounded-lg">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role Label *</label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value, name: e.target.value.toUpperCase().replace(/[^A-Z0-9]/gi, '_') })}
              placeholder="e.g., Lab Technician"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
            {form.name && <p className="text-xs text-gray-400 mt-1">System name: {form.name.toUpperCase().replace(/[^A-Z0-9]/gi, '_')}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g., Handles lab test processing"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Copy Permissions From</label>
            <Select
              value={form.copyFromRole}
              onChange={(v) => setForm({ ...form, copyFromRole: v })}
              options={[
                { value: '', label: 'Start with no permissions' },
                { value: '__ALL__', label: 'Start with all permissions' },
                ...existingRoles.map(r => ({ value: r.name, label: r.label })),
              ]}
              buttonClassName="px-3 py-2 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, color: c })}
                  className={`w-8 h-8 rounded-full bg-gradient-to-br ${getColorStyle(c).color} ${form.color === c ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-105'} transition-all`}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
            <div className="flex flex-wrap gap-2">
              {ICON_OPTIONS.map(iconName => {
                const Icon = ICON_MAP[iconName] || UserCog;
                return (
                  <button
                    key={iconName}
                    type="button"
                    onClick={() => setForm({ ...form, icon: iconName })}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center border-2 transition-all ${
                      form.icon === iconName ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                    title={iconName}
                  >
                    <Icon className="h-5 w-5" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
          <button onClick={onClose} className="px-5 py-2.5 text-gray-700 hover:bg-gray-200 rounded-lg font-medium">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={saving || !form.label.trim()}
            className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <RotateCcw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            <span>{saving ? 'Creating...' : 'Create Role'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RolesPermissions;
